import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useProfiles } from "../profiles/ProfileContext";
import { PageHeader, Panel } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { Button } from "../components/ui/Button";
import { labelize } from "../lib/format";
import { myLoads, laneLabel, LOAD_POST_TYPES, type MyLoad } from "../lib/loads";
import {
  listShipments, createShipment, updateShipmentStatus,
  SHIPMENT_STATUSES, type Shipment, type ShipmentStatus,
} from "../lib/dispatch";

// The next status a party can advance a shipment to (linear, plus "issue" from anywhere).
const NEXT: Partial<Record<ShipmentStatus, ShipmentStatus>> = {
  dispatched: "picked_up",
  picked_up: "in_transit",
  in_transit: "delivered",
};

export function Dispatch() {
  const { active } = useProfiles();
  const isPoster = active ? LOAD_POST_TYPES.includes(active.type) : false;
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [awarded, setAwarded] = useState<MyLoad[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [{ shipments }, mine] = await Promise.all([
      listShipments(),
      active && isPoster ? myLoads(active.id) : Promise.resolve({ loads: [] as MyLoad[] }),
    ]);
    setShipments(shipments);
    const dispatched = new Set(shipments.map((s) => s.load_id));
    setAwarded(mine.loads.filter((l) => l.status === "awarded" && !dispatched.has(l.id)));
  }, [active, isPoster]);

  useEffect(() => { void refresh().finally(() => setLoading(false)); }, [refresh]);

  async function dispatch(loadId: string) {
    setBusy(loadId);
    try { await createShipment(loadId); await refresh(); } finally { setBusy(null); }
  }

  async function advance(s: Shipment, status: ShipmentStatus) {
    setBusy(s.id);
    try { await updateShipmentStatus({ id: s.id, status }); await refresh(); } finally { setBusy(null); }
  }

  const active_ = shipments.filter((s) => s.status !== "delivered");
  const delivered = shipments.filter((s) => s.status === "delivered");

  return (
    <div className="grid gap-6">
      <PageHeader title="Dispatch" subtitle="Track awarded loads from pickup through delivery." />

      {isPoster && awarded.length > 0 && (
        <Panel title={`Ready to dispatch (${awarded.length})`}>
          <ul className="grid gap-2">
            {awarded.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-3 text-sm">
                <Link to={`/loads/${l.id}`} className="font-bold hover:text-accent-strong">{laneLabel(l.origin, l.destination)}</Link>
                <Button onClick={() => dispatch(l.id)} disabled={busy === l.id}>Dispatch</Button>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : shipments.length === 0 ? (
        <p className="font-mono text-sm text-muted">No active shipments yet. Awarded loads you dispatch will appear here.</p>
      ) : (
        <>
          <Panel title={`In transit (${active_.length})`}>
            {active_.length === 0 ? (
              <p className="font-mono text-sm text-muted">Nothing in transit.</p>
            ) : (
              <ul className="grid gap-3">
                {active_.map((s) => {
                  const next = NEXT[s.status];
                  return (
                    <li key={s.id} className="grid gap-2 rounded-card border border-line bg-surface p-3">
                      <div className="flex items-center justify-between gap-3">
                        <Link to={`/loads/${s.load_id}`} className="font-bold hover:text-accent-strong">{laneLabel(s.origin, s.destination)}</Link>
                        <StatusLabel status={s.status} />
                      </div>
                      <p className="font-mono text-[11px] text-muted">
                        {labelize(s.equipment_type)}{s.pickup_date ? ` · pickup ${s.pickup_date}` : ""} · {s.as_poster ? "you dispatched" : "you're hauling"}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {next && (
                          <Button variant="ghost" onClick={() => advance(s, next)} disabled={busy === s.id}>
                            Mark {labelize(next)}
                          </Button>
                        )}
                        {s.status !== "issue" && (
                          <button type="button" className="rounded border border-line px-2 py-0.5 font-mono text-[11px] text-danger hover:border-danger" onClick={() => advance(s, "issue")} disabled={busy === s.id}>
                            Flag issue
                          </button>
                        )}
                        {s.status === "issue" && (
                          <button type="button" className="rounded border border-line px-2 py-0.5 font-mono text-[11px] text-muted hover:border-accent" onClick={() => advance(s, "in_transit")} disabled={busy === s.id}>
                            Resume
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>

          {delivered.length > 0 && (
            <Panel title={`Delivered (${delivered.length})`}>
              <ul className="grid gap-1">
                {delivered.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 text-sm">
                    <Link to={`/loads/${s.load_id}`} className="hover:text-accent-strong">{laneLabel(s.origin, s.destination)}</Link>
                    <StatusLabel status={s.status} />
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </>
      )}

      <p className="sr-only">{SHIPMENT_STATUSES.join(",")}</p>
    </div>
  );
}
